import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Checkbox,
  IconButton,
  Paper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
} from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import Header from "../../components/GSO/Header";
import { useSidebar } from "../../components/SidebarContext";
import { auth } from "../../config/firebaseConfig";
import { getApiUrl, getApiHeaders } from "../../config/api";

const humanizeCategory = (cat) => {
  if (!cat) return "System";
  const map = {
    gso: "General Services Office",
    system: "System",
    claim: "Claims",
    account: "Account",
    item_match: "Item Match",
    announcement: "Announcement",
    update: "Update",
    alert: "Alert",
    match: "Match",
    verification: "Verification",
  };
  return map[String(cat).toLowerCase()] || cat;
};

const GSOMessages = () => {
  const [notifications, setNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // State for search query
  const [sortOrder, setSortOrder] = useState("asc"); // State for sorting order
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isSidebarOpen } = useSidebar();
  const isMobile = /Mobi|Android/i.test(navigator.userAgent) || window.innerWidth <= 600;

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const user = auth.currentUser;
      if (!user) {
        setError("Please login to view notifications");
        setNotifications([]);
        return;
      }
      const token = await user.getIdToken();
      const res = await fetch(getApiUrl("api/notifications?unread_only=false&limit=50"), {
        headers: getApiHeaders({ Authorization: `Bearer ${token}` })
      });
      if (!res.ok) {
        throw new Error("Failed to fetch notifications");
      }
      const data = await res.json();
      setNotifications((data.notifications || []).map(n => ({ ...n, checked: false })));
    } catch (e) {
      setError(e.message || "Error loading notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleCheckboxChange = (id) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, checked: !notif.checked } : notif
      )
    );
  };

  const handleSelectAll = () => {
    const newCheckedState = !selectAll;
    setSelectAll(newCheckedState);
    setNotifications((prev) =>
      prev.map((notif) => ({ ...notif, checked: newCheckedState }))
    );
  };

  const handleDelete = () => {
    const toDelete = notifications.filter(n => n.checked);
    if (toDelete.length === 0) return;
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    const toDelete = notifications.filter(n => n.checked);
    setSelectAll(false);
    // Delete from backend
    (async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;
        const token = await user.getIdToken();
        await Promise.all(
          toDelete.map((n) =>
            fetch(getApiUrl(`api/notifications/${n.id}`), {
              method: "DELETE",
              headers: getApiHeaders({ Authorization: `Bearer ${token}` }),
            })
          )
        );
      } catch (e) {
        // ignore delete errors for now
      } finally {
        setOpenDeleteDialog(false);
        fetchNotifications();
      }
    })();
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchNotifications();
    setTimeout(() => setIsRefreshing(false), 600);
  };

  // Filter and sort notifications
  const filteredNotifications = React.useMemo(() => {
    let filtered = notifications.filter(
      (notif) =>
        humanizeCategory(notif.category).toLowerCase().includes(searchQuery.toLowerCase()) ||
        (notif.message || "").toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sort notifications by date
    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || a.date);
      const dateB = new Date(b.created_at || b.date);
      return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
    });

    return filtered;
  }, [notifications, searchQuery, sortOrder]);

  return (
    <Box
      sx={{
  transition: "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), max-width 0.3s cubic-bezier(0.4,0,0.2,1)",
        marginLeft: isSidebarOpen ? (isMobile ? "200px" : "300px") : 0,
        bgcolor: "#FBFFE9",
        minHeight: "100vh",
        maxWidth: isSidebarOpen ? (isMobile ? "calc(100vw - 200px)" : "calc(100vw - 300px)") : "100vw",
        overflowX: "hidden",
        width: "100%",
      }}
    >
      <Header title="Notification" setSearchQuery={setSearchQuery} setSortOrder={setSortOrder} />

      <Box sx={{ px: 8, pt: 4 }}>

        <Paper
          sx={{
            p: 4,
            bgcolor: "#E1ECC8",
            borderRadius: "34px",
            position: "relative",
            minHeight: "600px",
            overflow: "hidden",
          }}
        >
          <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: "center", pl: 0 }}>
            <Checkbox
              sx={{
                width: 34,
                height: 34,
                padding: 0,
                color: "#526D50", // Default color
                "&.Mui-checked": {
                  color: "#526D50", // Dark green when checked
                },
              }}
              checked={selectAll}
              onChange={handleSelectAll}
            />
            <IconButton color="primary" onClick={handleRefresh}>
              <RefreshIcon 
                sx={{ 
                  width: 27, 
                  height: 27, 
                  color: "#526D50",
                  animation: isRefreshing ? 'spin 0.6s linear' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }} 
              />
            </IconButton>
            <IconButton color="primary" onClick={handleDelete}>
              <DeleteIcon sx={{ width: 27, height: 27, color: "#526D50" }} />
            </IconButton>
          </Stack>

          {loading ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {[1, 2, 3, 4, 5].map((item) => (
                <Stack key={item} direction="row" spacing={2} alignItems="flex-start">
                  <Skeleton variant="rectangular" width={34} height={34} sx={{ borderRadius: '4px' }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="30%" height={24} sx={{ mb: 1 }} />
                    <Skeleton variant="text" width="90%" height={20} />
                  </Box>
                  <Skeleton variant="text" width="150px" height={20} />
                </Stack>
              ))}
            </Stack>
          ) : error ? (
            <Typography sx={{ textAlign: "center", color: "#c74f4f", mt: 4 }}>{error}</Typography>
          ) : filteredNotifications.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#526D50", mt: 4 }}>No notifications</Typography>
          ) : (
            <Stack spacing={2}>
              {filteredNotifications.map((notification) => (
              <Stack
                key={notification.id}
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
                sx={{
                  padding: "10px 0",
                  borderRadius: "10px",
                  backgroundColor: notification.is_read ? 'transparent' : 'rgba(160, 196, 157, 0.15)',
                }}
              >
                {/* Left Section: Checkbox and Category */}
                <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ flex: 1 }}>
                  <Checkbox
                    sx={{
                      width: 34,
                      height: 34,
                      padding: 0,
                      color: "#526D50", // Default color
                      "&.Mui-checked": {
                        color: "#526D50", // Dark green when checked
                      },
                    }}
                    checked={notification.checked}
                    onChange={() => handleCheckboxChange(notification.id)}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        color: notification.is_read ? "#707070" : "#516d4f",
                        fontSize: 16,
                        fontWeight: notification.is_read ? 400 : 700,
                        mb: 0.5,
                      }}
                    >
                      {humanizeCategory(notification.category)}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: notification.is_read ? "#707070" : "#516d4f",
                        fontSize: 16,
                        fontWeight: notification.is_read ? 400 : 600,
                      }}
                    >
                      {notification.message}
                    </Typography>
                  </Box>
                </Stack>

                {/* Right Section: Date & Time */}
                <Typography
                  variant="body2"
                  sx={{
                    color: notification.is_read ? "#707070" : "#516d4f",
                    fontSize: 16,
                    fontWeight: notification.is_read ? 400 : 600,
                    textAlign: "right",
                    pl: 2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {new Date(notification.created_at || notification.date).toLocaleString()}
                </Typography>
              </Stack>
            ))}
          </Stack>
          )}
        </Paper>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            bgcolor: '#E1ECC8',
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#516d4f',
          fontWeight: 700,
          borderBottom: '2px solid #a0c49d',
          pb: 2
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
            Confirm Deletion
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ color: '#707070', lineHeight: 1.6 }}>
            Are you sure you want to delete {notifications.filter(n => n.checked).length} notification(s)? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            variant="outlined"
            sx={{
              borderColor: '#a0c49d',
              color: '#516d4f',
              borderRadius: '12px',
              px: 3,
              fontWeight: 600,
              '&:hover': {
                borderColor: '#8ab287',
                bgcolor: 'rgba(160, 196, 157, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              bgcolor: '#c74f4f',
              color: '#fff',
              borderRadius: '12px',
              px: 3,
              fontWeight: 600,
              '&:hover': {
                bgcolor: '#a33f3f',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GSOMessages;
